import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import HeroCarousel from '../components/HeroCarousel';
import CategoryGrid from '../components/CategoryGrid';
import PlaceCard from '../components/PlaceCard';
import AdCard from '../components/AdCard';
import PageLoader from '../components/PageLoader';
import ContactFooter from '../components/ContactFooter';
import { useCategories } from '../hooks/useCategories';
import { usePaginatedPlaces } from '../hooks/usePaginatedPlaces';
import { useHeroSlides } from '../hooks/useHeroSlides';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useLocation } from '../context/LocationContext';
import { getAds } from '../services/api';
import telegramService from '../services/telegram';
import { MOSAIC_SKELETON } from '../utils/categoryMosaic';

// Размер страницы. Запрашивается с сервера, а не режется на клиенте:
// раньше грузились все 100 мест разом, и при росте базы первый экран
// ждал бы всю выдачу
const FEED_PAGE_SIZE = 12;

/**
 * Раскладывает рекламу между карточками мест.
 *
 * Считает от начала загруженного списка, поэтому при подгрузке следующей
 * страницы позиции уже показанной рекламы не съезжают — добавляется только
 * новая, дальше по ленте.
 */
const buildFeed = (visiblePlaces, ads) => {
  const result = [];
  const adShowCounts = {};
  let nextAdIndex = 0;
  let placesUntilNextAd = ads.length > 0 ? (ads[0].homeShowEveryNPlaces || 3) : 0;

  ads.forEach(ad => {
    adShowCounts[ad.id] = 0;
  });

  visiblePlaces.forEach((place) => {
    result.push(<PlaceCard key={place.id} place={place} />);

    if (ads.length > 0) {
      placesUntilNextAd--;

      if (placesUntilNextAd === 0 && nextAdIndex < ads.length) {
        const currentAd = ads[nextAdIndex];

        if (adShowCounts[currentAd.id] < (currentAd.homeRepeatCount || 1)) {
          result.push(<AdCard key={`ad-${currentAd.id}-${adShowCounts[currentAd.id]}`} ad={currentAd} source="home" />);
          adShowCounts[currentAd.id]++;
          placesUntilNextAd = currentAd.homeShowEveryNPlaces || 3;
        }

        if (adShowCounts[currentAd.id] >= (currentAd.homeRepeatCount || 1)) {
          nextAdIndex++;
          if (nextAdIndex < ads.length) {
            placesUntilNextAd = ads[nextAdIndex].homeShowEveryNPlaces || 3;
          }
        }
      }
    }
  });

  return result;
};

const Home = () => {
  const navigate = useNavigate();
  const scrollRef = useScrollRestoration();
  const { categories, loading: categoriesLoading } = useCategories();
  const { slides, loading: slidesLoading } = useHeroSlides();
  const {
    places,
    loading: placesLoading,
    hasMore,
    loadMore,
  } = usePaginatedPlaces(FEED_PAGE_SIZE);
  const { hasLocation, requestUserLocation, loading: locationLoading } = useLocation();
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [ads, setAds] = useState([]);

  // Загрузка рекламы для главной
  useEffect(() => {
    const loadAds = async () => {
      const response = await getAds(null, 'home');
      const sortedAds = (response.data || []).sort((a, b) => (a.homeOrder || 0) - (b.homeOrder || 0));
      setAds(sortedAds);
    };
    loadAds();
  }, []);

  const feed = useMemo(() => buildFeed(places, ads), [places, ads]);

  // Подгрузка при прокрутке. Наблюдаем за меткой под лентой: как только она
  // подходит к экрану, запрашиваем следующую страницу. rootMargin 400px —
  // порция готова до того, как пользователь упрётся в конец списка.
  //
  // Ref именно callback, а не useRef+useEffect: метка появляется в разметке
  // только после загрузки мест, то есть позже первого эффекта, и обычный ref
  // на момент подписки был бы ещё null — наблюдатель не навешивался вовсе
  const observerRef = useRef(null);

  // loadMore меняется вместе с hasMore. Держим его в ref, чтобы подписка
  // оставалась стабильной и наблюдатель не пересоздавался на каждую порцию
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observerRef.current.observe(node);
  }, []);

  // Снять наблюдателя при уходе со страницы
  useEffect(() => () => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);

  const handleNearbySearch = async () => {
    telegramService.hapticFeedback('medium');

    if (hasLocation()) {
      navigate('/nearby');
    } else {
      setIsRequestingLocation(true);
      try {
        await requestUserLocation();
        navigate('/nearby');
      } catch (error) {
        navigate('/nearby');
      } finally {
        setIsRequestingLocation(false);
      }
    }
  };

  const isLoading = categoriesLoading || slidesLoading || placesLoading;

  return (
    <PageLoader loading={isLoading}>
      {/* Все секции по одной линии — поля экрана px-4 (16px), брендбук 10.1.
          Вертикальный ритм между секциями один: mt-8 (32px) */}
      <div ref={scrollRef} className="min-h-screen bg-theme-bg pb-6 max-w-app mx-auto" style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height))' }}>
      <Header />

      {/* Hero Carousel */}
      <HeroCarousel slides={slides} loading={slidesLoading} />

      {/* Categories Grid */}
      <section className="mt-8 px-4">
        <h2 className="mb-4 text-h2 text-theme-text-primary">
          Категории
        </h2>
        {categoriesLoading ? (
          <div className="grid grid-cols-3 gap-2">
            {MOSAIC_SKELETON.map((style, i) => (
              <div
                key={i}
                className="bg-theme-card-secondary rounded-xl animate-pulse"
                style={style}
              />
            ))}
          </div>
        ) : (
          <CategoryGrid categories={categories} />
        )}
      </section>

      {/* CTA Button */}
      <section className="mt-8 px-4">
          <button
            onClick={handleNearbySearch}
            disabled={isRequestingLocation || locationLoading}
            className="w-full h-12 bg-action text-on-action rounded-md border-none text-h3 text-center cursor-pointer flex items-center justify-center gap-2 transition-all duration-fast hover:opacity-90 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRequestingLocation || locationLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                <span>Получение геолокации...</span>
              </>
            ) : (
              <span>Искать места рядом с вами</span>
            )}
          </button>
      </section>

      {/* Latest Places */}
      <section className="mt-8 px-4">
        <h2 className="mb-4 text-h2 text-theme-text-primary">
          Новое
        </h2>
        {placesLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-theme-card-secondary rounded-md h-48 animate-pulse" />
            ))}
          </div>
        ) : places.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              {feed}
            </div>
            {/* Метка для наблюдателя: подгружает следующую порцию.
                Скелетоны показывают, что список продолжается — иначе
                при быстрой прокрутке низ выглядит как конец ленты */}
            {hasMore && (
              <div ref={sentinelRef} className="grid grid-cols-2 gap-4 mt-4">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-theme-card-secondary rounded-md h-48 animate-pulse" />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="bg-theme-card-secondary rounded-md p-6 text-center">
            <p className="text-theme-text-secondary text-body">Нет доступных мест</p>
            <p className="text-caption text-theme-text-hint mt-2">
              Добавьте данные через админ-панель Strapi
            </p>
          </div>
        )}
      </section>

      {/* Поля задаёт страница: у самого блока их нет, иначе на экранах,
          где он лежит внутри контейнера с отступом, они удваиваются */}
      <div className="px-4">
        <ContactFooter />
      </div>
    </div>
    </PageLoader>
  );
};

export default Home;
